import React, { useState } from 'react';
import { FaSearch, FaSpinner, FaQrcode, FaEnvelope, FaExclamationTriangle, FaUserPlus, FaTimes } from 'react-icons/fa';

/**
 * Componente de contenido de FindTicketPage que puede ser usado tanto en la página real como en el preview
 * @param {Object} props
 * @param {Object} props.marca - Datos de la marca
 * @param {Object} props.texts - Textos personalizados
 * @param {Object} props.styles - Estilos personalizados
 * @param {string} props.layout - Layout seleccionado ('hero', 'minimal', 'centered')
 * @param {Object} props.layoutOptions - Opciones de layout
 * @param {Object} [props.imagenFondo] - Imagen de fondo de la personalización (con url, cdnUrl, etc.)
 * @param {Function} props.onSearch - Callback cuando se busca (opcional, para preview)
 * @param {Function} props.onJoinClub - Callback cuando se une al club (opcional, para preview)
 * @param {boolean} props.isPreview - Si es true, desactiva funcionalidad interactiva
 */
const FindTicketPageContent = ({
    marca,
    texts,
    styles = {},
    layout = 'centered',
    layoutOptions = {},
    imagenFondo = null,
    onSearch,
    onJoinClub,
    isPreview = false
}) => {
    const [searching, setSearching] = useState(false);
    const [email, setEmail] = useState('');
    const [ticketNotFound, setTicketNotFound] = useState(false);

    // Calcular estilos aplicando personalización
    const headerBgColor = styles.headerBackgroundColor || '#4F46E5';
    const headerTextColor = styles.headerTextColor || '#FFFFFF';
    const pageBgColor = styles.pageBackgroundColor || '#F9FAFB';
    const cardBgColor = styles.cardBackgroundColor || '#FFFFFF';
    const cardBorderRadius = styles.cardBorderRadius || '16px';
    const titleColor = styles.titleColor || '#111827';
    const descriptionColor = styles.descriptionColor || '#4B5563';
    const buttonBgColor = styles.buttonBackgroundColor || headerBgColor;
    const buttonTextColor = styles.buttonTextColor || '#FFFFFF';
    const inputBorderColor = styles.inputBorderColor || '#D1D5DB';
    const inputFocusColor = styles.inputFocusColor || '#3B82F6';
    const errorBackgroundColor = styles.errorBackgroundColor || '#FEF3C7';
    const errorBorderColor = styles.errorBorderColor || '#FCD34D';
    const errorTextColor = styles.errorTextColor || '#92400E';
    const errorIconColor = styles.errorIconColor || '#F59E0B';
    const fontFamily = styles.fontFamily || 'sans-serif';
    const fontSize = styles.fontSize || '16px';

    // Determinar clases CSS según layout y opciones
    const headerClasses = {
        full: 'w-full shadow-md',
        minimal: 'w-full shadow-sm',
        transparent: 'w-full bg-transparent'
    }[layoutOptions.headerStyle] || 'w-full shadow-md';

    const cardClasses = {
        elevated: 'shadow-lg',
        flat: 'shadow-none',
        outlined: 'border-2 border-gray-200 shadow-none'
    }[layoutOptions.cardStyle] || 'shadow-lg';

    const logoPositionClasses = {
        left: 'justify-start',
        center: 'justify-center',
        right: 'justify-end'
    }[layoutOptions.logoPosition] || 'justify-center';

    const handleSearch = async (e) => {
        e.preventDefault();
        if (isPreview) {
            // En preview, solo mostrar el estado de "no encontrado" para demostración
            setSearching(true);
            setTimeout(() => {
                setSearching(false);
                setTicketNotFound(true);
            }, 1000);
            return;
        }

        if (!email.trim()) return;

        setSearching(true);
        setTicketNotFound(false);

        if (onSearch) {
            try {
                await onSearch(email.trim().toLowerCase());
            } catch (error) {
                setTicketNotFound(true);
            } finally {
                setSearching(false);
            }
        }
    };

    const handleJoinClub = () => {
        if (isPreview) {
            // En preview, solo resetear el estado
            setTicketNotFound(false);
            setEmail('');
            return;
        }

        if (onJoinClub) {
            onJoinClub();
        }
    };

    const handleCancel = () => {
        setTicketNotFound(false);
        setEmail('');
    };

    // Renderizar según el layout seleccionado
    const renderContent = () => {
        if (layout === 'hero') {
            // Layout Hero: Header grande con imagen de fondo
            return (
                <>
                    {/* Hero Header */}
                    <div
                        className="relative w-full h-96 flex items-center justify-center"
                        style={{
                            background: imagenFondo
                                ? `linear-gradient(135deg, ${headerBgColor} 0%, ${headerBgColor}dd 100%), url(${imagenFondo.cdnUrl || imagenFondo.url})`
                                : `linear-gradient(135deg, ${headerBgColor} 0%, ${headerBgColor}dd 100%)`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundBlendMode: imagenFondo ? 'overlay' : 'normal'
                        }}
                    >
                        {/* Overlay oscuro - solo si está habilitado o si no hay imagen de fondo */}
                        {layoutOptions.showOverlay !== false && (
                            <div className="absolute inset-0 bg-black bg-opacity-40"></div>
                        )}
                        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
                            {layoutOptions.showLogo && marca?.logotipo && (
                                <img
                                    src={marca.logotipo.cdnUrl || marca.logotipo.url}
                                    alt={`Logo de ${marca.nombre}`}
                                    className="h-24 w-24 object-contain bg-white rounded-lg shadow-lg p-3 mx-auto mb-6"
                                />
                            )}
                            {layoutOptions.showBrandName !== false && (
                                <h1
                                    className="text-4xl md:text-5xl font-extrabold mb-4"
                                    style={{ color: '#FFFFFF' }}
                                >
                                    {marca?.nombre}
                                </h1>
                            )}
                            {layoutOptions.showSlogan && marca?.eslogan && (
                                <p className="text-xl text-white opacity-90 mb-8">
                                    {marca.eslogan}
                                </p>
                            )}
                            {/* Icono QR - solo si está habilitado */}
                            {layoutOptions.showQrIcon !== false && (
                                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-white bg-opacity-20 backdrop-blur-sm mb-6">
                                    <FaQrcode className="text-4xl text-white" />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Main Content - Hero Style */}
                    <div className="max-w-2xl mx-auto px-4 -mt-20 relative z-20">
                        <div
                            className={`bg-white rounded-2xl ${cardClasses} p-8 md:p-10 shadow-2xl`}
                            style={{
                                backgroundColor: cardBgColor,
                                borderRadius: cardBorderRadius
                            }}
                        >
                            <div className="text-center mb-8">
                                <h2
                                    className="text-3xl md:text-4xl font-bold mb-3"
                                    style={{ color: titleColor }}
                                >
                                    {texts.pageTitle}
                                </h2>
                                <p className="text-lg" style={{ color: descriptionColor }}>
                                    {texts.pageDescription}
                                </p>
                            </div>
                            {renderForm()}
                        </div>
                    </div>
                </>
            );
        } else if (layout === 'minimal') {
            // Layout Minimal: Diseño limpio y minimalista
            return (
                <>
                    {/* Minimal Header */}
                    {layoutOptions.headerStyle !== 'transparent' && (
                        <div
                            className={`${headerClasses} py-3`}
                            style={{ backgroundColor: headerBgColor }}
                        >
                            <div className="max-w-4xl mx-auto px-4">
                                <div className={`flex items-center ${logoPositionClasses}`}>
                                    {layoutOptions.showLogo && marca?.logotipo && (
                                        <img
                                            src={marca.logotipo.cdnUrl || marca.logotipo.url}
                                            alt={`Logo de ${marca.nombre}`}
                                            className="h-10 w-10 object-contain mr-3"
                                        />
                                    )}
                                    {layoutOptions.showBrandName !== false && (
                                        <h1
                                            className="text-xl font-semibold"
                                            style={{ color: headerTextColor }}
                                        >
                                            {marca?.nombre}
                                        </h1>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Main Content - Minimal Style */}
                    <div className="max-w-xl mx-auto px-4 py-12">
                        <div className="text-center mb-8">
                            <h2
                                className="text-2xl font-bold mb-2"
                                style={{ color: titleColor }}
                            >
                                {texts.pageTitle}
                            </h2>
                            <p className="text-sm" style={{ color: descriptionColor }}>
                                {texts.pageDescription}
                            </p>
                        </div>
                        <div
                            className={`bg-white rounded-lg ${cardClasses === 'shadow-lg' ? 'shadow-sm' : cardClasses} p-6`}
                            style={{
                                backgroundColor: cardBgColor,
                                borderRadius: cardBorderRadius
                            }}
                        >
                            {renderForm()}
                        </div>
                    </div>
                </>
            );
        } else {
            // Layout Centered: Diseño centrado estándar (default)
            return (
                <>
                    {/* Header */}
                    {layoutOptions.headerStyle !== 'transparent' && (
                        <div
                            className={headerClasses}
                            style={{ backgroundColor: headerBgColor }}
                        >
                            <div className="max-w-4xl mx-auto px-4 py-6">
                                <div className={`flex items-center ${logoPositionClasses}`}>
                                    {layoutOptions.showLogo && marca?.logotipo && (
                                        <img
                                            src={marca.logotipo.cdnUrl || marca.logotipo.url}
                                            alt={`Logo de ${marca.nombre}`}
                                            className="h-16 w-16 object-contain bg-white rounded-lg shadow-sm p-2 mr-4"
                                        />
                                    )}
                                    <div className={layoutOptions.logoPosition === 'center' ? 'text-center' : ''}>
                                        {layoutOptions.showBrandName !== false && (
                                            <h1
                                                className="text-2xl md:text-3xl font-bold"
                                                style={{ color: headerTextColor }}
                                            >
                                                {marca?.nombre}
                                            </h1>
                                        )}
                                        {layoutOptions.showSlogan && marca?.eslogan && (
                                            <p
                                                className="text-sm mt-1 opacity-90"
                                                style={{ color: headerTextColor }}
                                            >
                                                {marca.eslogan}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Main Content - Centered Style */}
                    <div className="max-w-2xl mx-auto px-4 py-8">
                        <div
                            className={`bg-white rounded-2xl ${cardClasses} p-6 md:p-8`}
                            style={{
                                backgroundColor: cardBgColor,
                                borderRadius: cardBorderRadius
                            }}
                        >
                            {/* Title Section */}
                            <div className="text-center mb-8">
                                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
                                    <FaQrcode className="text-3xl text-blue-600" />
                                </div>
                                <h2
                                    className="text-2xl md:text-3xl font-bold mb-2"
                                    style={{ color: titleColor }}
                                >
                                    {texts.pageTitle}
                                </h2>
                                <p style={{ color: descriptionColor }}>
                                    {texts.pageDescription}
                                </p>
                            </div>
                            {renderForm()}
                        </div>
                    </div>
                </>
            );
        }
    };

    // Función para renderizar el formulario (reutilizable)
    const renderForm = () => (
        <>
            {/* Search Form */}
            <form onSubmit={handleSearch} className="space-y-6">
                <div>
                    <label
                        htmlFor="email"
                        className="block text-sm font-medium mb-2"
                        style={{ color: titleColor }}
                    >
                        {texts.emailLabel}
                    </label>
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FaEnvelope className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                            type="email"
                            id="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="block w-full pl-10 pr-3 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-colors"
                            style={{
                                borderColor: inputBorderColor,
                                focusRingColor: inputFocusColor
                            }}
                            placeholder={texts.emailPlaceholder}
                            required
                            disabled={searching}
                        />
                    </div>
                    {texts.emailHelpText && layout !== 'minimal' && (
                        <p className="mt-2 text-sm" style={{ color: descriptionColor }}>
                            {texts.emailHelpText}
                        </p>
                    )}
                </div>

                <button
                    type="submit"
                    disabled={searching || !email.trim()}
                    className="w-full flex items-center justify-center px-6 py-3 rounded-lg font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                    style={{
                        backgroundColor: buttonBgColor,
                        color: buttonTextColor,
                        focusRingColor: buttonBgColor
                    }}
                >
                    {searching ? (
                        <>
                            <FaSpinner className="animate-spin mr-2" />
                            {texts.searchingButtonText}
                        </>
                    ) : (
                        <>
                            <FaSearch className="mr-2" />
                            {texts.searchButtonText}
                        </>
                    )}
                </button>
            </form>

            {/* Ticket Not Found - Join Club Option */}
            {ticketNotFound && (
                <div
                    className="mt-8 p-6 rounded-lg"
                    style={{
                        backgroundColor: errorBackgroundColor,
                        borderColor: errorBorderColor,
                        borderWidth: '1px',
                        borderStyle: 'solid'
                    }}
                >
                    <div className="flex items-start">
                        <div className="flex-shrink-0">
                            <FaExclamationTriangle
                                className="h-6 w-6"
                                style={{ color: errorIconColor }}
                            />
                        </div>
                        <div className="ml-3 flex-1">
                            <h3
                                className="text-lg font-semibold mb-2"
                                style={{ color: errorTextColor }}
                            >
                                {texts.ticketNotFoundTitle}
                            </h3>
                            <p
                                className="text-sm mb-4"
                                style={{ color: errorTextColor }}
                            >
                                {texts.ticketNotFoundMessage}
                            </p>
                            <p
                                className="text-sm font-medium mb-4"
                                style={{ color: errorTextColor }}
                            >
                                {texts.joinClubQuestion}
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3">
                                <button
                                    onClick={handleJoinClub}
                                    className="flex-1 flex items-center justify-center px-4 py-2 rounded-lg hover:opacity-90 transition-colors font-medium"
                                    style={{
                                        backgroundColor: buttonBgColor,
                                        color: buttonTextColor
                                    }}
                                >
                                    <FaUserPlus className="mr-2" />
                                    {texts.joinClubButtonText}
                                </button>
                                <button
                                    onClick={handleCancel}
                                    className="flex-1 flex items-center justify-center px-4 py-2 border rounded-lg hover:opacity-90 transition-colors font-medium"
                                    style={{
                                        backgroundColor: cardBgColor,
                                        color: buttonBgColor,
                                        borderColor: buttonBgColor
                                    }}
                                >
                                    <FaTimes className="mr-2" />
                                    {texts.cancelButtonText}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Help Section - Solo mostrar si no hay error y no es minimal */}
            {!ticketNotFound && layout !== 'minimal' && (
                <div className="mt-8 pt-6 border-t" style={{ borderColor: inputBorderColor }}>
                    <h3
                        className="text-sm font-semibold mb-3"
                        style={{ color: titleColor }}
                    >
                        {texts.helpSectionTitle}
                    </h3>
                    <ul className="space-y-2 text-sm" style={{ color: descriptionColor }}>
                        <li className="flex items-start">
                            <span className="mr-2" style={{ color: buttonBgColor }}>•</span>
                            <span>{texts.helpItem1}</span>
                        </li>
                        <li className="flex items-start">
                            <span className="mr-2" style={{ color: buttonBgColor }}>•</span>
                            <span>{texts.helpItem2}</span>
                        </li>
                        <li className="flex items-start">
                            <span className="mr-2" style={{ color: buttonBgColor }}>•</span>
                            <span>{texts.helpItem3}</span>
                        </li>
                    </ul>
                </div>
            )}
        </>
    );

    if (!marca) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center text-gray-600">
                    <p className="text-xl">Marca no disponible en preview</p>
                </div>
            </div>
        );
    }

    return (
        <div
            className="min-h-screen"
            style={{
                fontFamily,
                fontSize,
                backgroundColor: layout === 'hero' ? '#000000' : pageBgColor,
                background: layout === 'hero' ? 'none' : `linear-gradient(to bottom right, ${pageBgColor}, #F3F4F6)`
            }}
        >
            {renderContent()}
        </div>
    );
};

export default FindTicketPageContent;

